import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import { notification } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { IFetchResponse } from '~utilities/fetch';
import { renderNotificationLoadingIcon } from '~utilities/utils';
import {
    ILoanDrawdownConditionDeleteAction,
    ILoanDrawdownConditionDocumentDeleteAction,
    ILoanDrawdownConditionDocumentsAddAction,
    ILoanDrawdownConditionDocumentsListAction,
    ILoanDrawdownConditionSuggestionsListAction,
    ILoanDrawdownConditionsAddAction,
    ILoanDrawdownConditionsListAction,
    ILoanDrawdownConditionsWithDocumentsListAction,
    loanDrawdownConditionDocumentSetAction,
    loanDrawdownConditionDocumentsListAction,
    loanDrawdownConditionDocumentsSetAction,
    loanDrawdownConditionSetAction,
    loanDrawdownConditionSuggestionsSetAction,
    loanDrawdownConditionsListAction,
    loanDrawdownConditionsSetAction,
} from './actions';
import LoansActionsEnum from './ActionsEnum';
import {
    loanDrawdownConditionDeleteRequest,
    loanDrawdownConditionDocumentDeleteRequest,
    loanDrawdownConditionDocumentsAddRequest,
    loanDrawdownConditionDocumentsListRequest,
    loanDrawdownConditionSuggestionsListRequest,
    loanDrawdownConditionsAddRequest,
    loanDrawdownConditionsListRequest,
} from '~Api/LoanDrawdowns/requests';
import ILoanDrawdownConditionDocument from '~Api/LoanDrawdowns/ILoanDrawdownConditionDocument';
import {
    parseLoanDrawdownCondition,
    parseLoanDrawdownConditionDocument,
    parseLoanDrawdownConditionSuggestion,
} from '~Api/LoanDrawdowns/parsers';
import ILoanDrawdownCondition from '~Api/LoanDrawdowns/ILoanDrawdownCondition';
import IConditionSuggestion from '~Api/LoanDrawdowns/IConditionSuggestion';

function* loanDrawdownConditionsWithDocumentsList(action: ILoanDrawdownConditionsWithDocumentsListAction): Iterator<unknown> {
    yield put(loanDrawdownConditionsListAction(action.drawdownUuid));
    yield put(loanDrawdownConditionDocumentsListAction(action.drawdownUuid));
}

function *loanDrawdownConditionsList(action: ILoanDrawdownConditionsListAction): Iterator<unknown> {
    const drawdownConditionsResponse: IFetchResponse = yield call(loanDrawdownConditionsListRequest, action.drawdownUuid);
    const drawdownConditions: ILoanDrawdownCondition[] = yield Promise.all(drawdownConditionsResponse.body.map(parseLoanDrawdownCondition));
    yield put(loanDrawdownConditionsSetAction(action.drawdownUuid, drawdownConditions));
}

function* loanDrawdownConditionsAdd(action: ILoanDrawdownConditionsAddAction): Iterator<unknown> {
    const key: string = `loanDrawdownConditionAdd ${dayjs().format()}`;
    const message: string = 'Add Drawdown Condition';

    notification.open({
        description: 'Adding drawdown condition...',
        duration: 0,
        icon: renderNotificationLoadingIcon(),
        key,
        message,
    });

    const loanDrawdownConditionAddResponse: IFetchResponse = yield call(loanDrawdownConditionsAddRequest, action.drawdownUuid, action.loanDrawdownCondition);
    if (loanDrawdownConditionAddResponse.status === 422) {
        notification.error({
            description: `There was a problem adding the drawdown condition: ${_.values(loanDrawdownConditionAddResponse.body)[0]}.`,
            duration: 0,
            key,
            message,
        });
    } else {
        const drawdownCondition: ILoanDrawdownCondition = yield parseLoanDrawdownCondition(loanDrawdownConditionAddResponse.body);
        yield put(loanDrawdownConditionSetAction(drawdownCondition));
        notification.success({
            description: 'The drawdown condition has been added.',
            duration: 4.5,
            key,
            message,
        });
    }
}

function* loanDrawdownConditionDelete(action: ILoanDrawdownConditionDeleteAction): Iterator<unknown> {
    yield call(loanDrawdownConditionDeleteRequest, action.loanDrawdownConditionUuid);
}

function *loanDrawdownConditionDocumentsList(action: ILoanDrawdownConditionDocumentsListAction): Iterator<unknown> {
    const drawdownConditionDocumentsResponse: IFetchResponse = yield call(loanDrawdownConditionDocumentsListRequest, action.drawdownUuid);
    const drawdownConditionDocuments: ILoanDrawdownConditionDocument[] = yield Promise.all(drawdownConditionDocumentsResponse.body.map(parseLoanDrawdownConditionDocument));
    yield put(loanDrawdownConditionDocumentsSetAction(action.drawdownUuid, drawdownConditionDocuments));
}

function* loanDrawdownConditionDocumentsAdd(action: ILoanDrawdownConditionDocumentsAddAction): Iterator<unknown> {
    const key: string = `loanDrawdownConditionDocumentsAdd ${dayjs().format()}`;
    const message: string = 'Add Document';

    notification.open({
        description: 'Adding document...',
        duration: 0,
        icon: renderNotificationLoadingIcon(),
        key,
        message,
    });

    const loanDrawdownConditionDocumentsAddResponse: IFetchResponse = yield call(loanDrawdownConditionDocumentsAddRequest, action.loanDrawdownConditionUuid, action.file);

    if (loanDrawdownConditionDocumentsAddResponse.status === 422) {
        notification.error({
            description: `There was a problem adding the document: ${_.values(loanDrawdownConditionDocumentsAddResponse.body)[0]}.`,
            duration: 0,
            key,
            message,
        });
    } else {
        const conditionDocument: ILoanDrawdownConditionDocument = parseLoanDrawdownConditionDocument(loanDrawdownConditionDocumentsAddResponse.body);
        yield put(loanDrawdownConditionDocumentSetAction(conditionDocument));

        notification.success({
            description: 'The document has been added.',
            duration: 4.5,
            key,
            message,
        });
    }
}

function* loanDrawdownConditionDocumentDelete(action: ILoanDrawdownConditionDocumentDeleteAction): Iterator<unknown> {
    yield call(loanDrawdownConditionDocumentDeleteRequest, action.loanDrawdownConditionDocumentUuid);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function* loanDrawdownConditionSuggestionsList(action: ILoanDrawdownConditionSuggestionsListAction): Iterator<unknown> {
    const loanDrawdownConditionSuggestionsListResponse: IFetchResponse = yield call(loanDrawdownConditionSuggestionsListRequest, action.drawdownUuid);
    const suggestions: IConditionSuggestion[] = yield Promise.all(loanDrawdownConditionSuggestionsListResponse.body.map(parseLoanDrawdownConditionSuggestion));
    yield put(loanDrawdownConditionSuggestionsSetAction(action.drawdownUuid, suggestions));
}

export function* LoanDrawdownsSagas(): Iterator<unknown> {
    yield all([
        takeEvery(LoansActionsEnum.LoanDrawdownConditionsWithDocumentsList, loanDrawdownConditionsWithDocumentsList),

        takeEvery(LoansActionsEnum.LoanDrawdownConditionsList, loanDrawdownConditionsList),
        takeEvery(LoansActionsEnum.LoanDrawdownConditionsAdd, loanDrawdownConditionsAdd),
        takeEvery(LoansActionsEnum.LoanDrawdownConditionDelete, loanDrawdownConditionDelete),

        takeEvery(LoansActionsEnum.LoanDrawdownConditionDocumentsList, loanDrawdownConditionDocumentsList),
        takeEvery(LoansActionsEnum.LoanDrawdownConditionDocumentsAdd, loanDrawdownConditionDocumentsAdd),
        takeEvery(LoansActionsEnum.LoanDrawdownConditionDocumentDelete, loanDrawdownConditionDocumentDelete),

        takeEvery(LoansActionsEnum.LoanDrawdownConditionSuggestionsList, loanDrawdownConditionSuggestionsList),
    ]);
}
